import { handleActions } from 'redux-actions'
import {
  confirmMediaSuccess,
  createApartmentSuccess,
  deleteApartmentMediaSuccess,
  deselectApartment,
  getAllMediaSuccess,
  getApartmentMediaSuccess,
  getApartmentSuccess,
  listApartments,
  listApartmentsFail,
  listApartmentsSuccess,
  removeApartmentSuccess,
  selectApartment,
  updateApartmentAdminInfoSuccess,
  updateApartmentClientInformationSuccess,
  updateApartmentClientInformationWithStatusSuccess,
  updateApartmentClientInformationToOverviewSuccess,
  updateApartmentConfirmationSuccess,
  updateApartmentInformationSuccess,
  updateStatusSuccess,
  updateApartmentUserIdSuccess,
  saveBeforeLoginApartmentInfoSuccess,
  saveInstantRentApplicationInfoSuccess,
  addBeforeLoginApartmentMediaSuccess,
  deleteBeforeLoginApartmentMediaSuccess,
  reorderBeforeLoginApartmentMediaSuccess,
  confirmBeforeLoginApartmentMediaSuccess,
  confirmBeforeLoginApartmentSuccess,
  signUpBeforeLoginSuccess,
  initializeBeforeLoginApartmentSuccess,
  emptyBeforeLoginState,
  getApartmentByCodeSuccess,
  updateApartmentMediaSuccess,
  searchApartments,
  searchApartmentsFail,
  searchApartmentsSuccess,
  addSwitchedData
} from '../actions/apartments'
import { getIdFromS3Key } from '../libs/strings'
import { BucketObject } from '../services/storage/types'
import { Apartment, BeforeLoginApartment } from '../types/apartments'
import { ImageFileWithObjectURL } from '../types/files'
import { ApartmentIdIndex, ApartmentsState } from '../types/states'
import { Action } from './types'

const initialState: ApartmentsState = {
  apartments: {}, // indexed by apartment id
  files: {}, // indexed by apartment id
  beforeLoginApartment: undefined,
  beforeLoginMedia: [],
  isApartmentsSearchLoading: false,
  publicApartment: null,
  isApartmentsLoading: false
}

const apartmentsReducer = handleActions<ApartmentsState, any>(
  {
    [selectApartment]: (
      state,
      { payload: selectedApartmentId }: Action<string>
    ) => ({
      ...state,
      selectedApartmentId
    }),
    [deselectApartment]: (state) => ({
      ...state,
      selectedApartmentId: undefined
    }),
    [createApartmentSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: apartment
      }
    }),
    [getApartmentSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null,
          media: apartment.media
            ? apartment.media
            : (state.files &&
              Array.isArray(state.files[apartment.apartmentId]) &&
              (state.files[apartment.apartmentId] || []).map(
                (file: { key: string }) => ({
                  id: getIdFromS3Key(file.key),
                  key: file.key
                })
              )) ||
            []
        }
      }
    }),
    [getApartmentByCodeSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      publicApartment: apartment
    }),
    [removeApartmentSuccess]: (
      state,
      { payload: apartmentId }: Action<string>
    ) => {
      const apartments = { ...state.apartments }
      delete apartments[apartmentId]
      return {
        ...state,
        apartments
      }
    },
    [listApartments]: (state) => ({
      ...state,
      isApartmentsLoading: true
    }),
    [listApartmentsFail]: (state) => ({
      ...state,
      isApartmentsLoading: false
    }),
    [listApartmentsSuccess]: (
      state,
      { payload: apartmentsArray }: Action<Apartment[]>
    ) => {
      const apartments = apartmentsArray.reduce((apartmentsAcc, apartment) => {
        apartmentsAcc[apartment.apartmentId] = {
          ...apartment
        }
        return apartmentsAcc
      }, {})
      return {
        ...state,
        apartments,
        isApartmentsLoading: false
      }
    },
    [searchApartments]: (state) => ({
      ...state,
      isApartmentsSearchLoading: true
    }),
    [searchApartmentsFail]: (state) => ({
      ...state,
      isApartmentsSearchLoading: false
    }),
    [searchApartmentsSuccess]: (
      state,
      { payload: apartmentsArray }: Action<Apartment[]>
    ) => {
      const apartments = apartmentsArray.reduce((apartmentsAcc, apartment) => {
        apartmentsAcc[apartment.apartmentId] = {
          ...apartment
        }
        return apartmentsAcc
      }, {})
      return {
        ...state,
        apartments,
        isApartmentsSearchLoading: false
      }
    },
    [updateApartmentClientInformationSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [updateApartmentClientInformationToOverviewSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [updateApartmentClientInformationWithStatusSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [updateApartmentInformationSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [updateApartmentAdminInfoSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [updateApartmentConfirmationSuccess]: (
      state,
      { payload: apartment }: Action
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [updateApartmentUserIdSuccess]: (
      state,
      { payload: apartment }: Action
    ) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [confirmMediaSuccess]: (state, { payload: apartment }: Action) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [updateStatusSuccess]: (state, { payload: apartment }: Action) => ({
      ...state,
      apartments: {
        ...state.apartments,
        [apartment.apartmentId]: {
          ...apartment,
          switchedData:
            state?.apartments[apartment.apartmentId]?.switchedData || null
        }
      }
    }),
    [getApartmentMediaSuccess]: (
      state,
      { payload: { apartmentId, bucketObjectsWithUrl } }
    ) => ({
      ...state,
      files: {
        ...state.files,
        [apartmentId]: bucketObjectsWithUrl
      },
      apartments: {
        ...state.apartments,
        [apartmentId]: {
          ...(state.apartments[apartmentId] || {}),
          media:
            (state.apartments[apartmentId] &&
              state.apartments[apartmentId].media) ||
            bucketObjectsWithUrl.map((file: { key: string }) => ({
              id: getIdFromS3Key(file.key),
              key: file.key
            }))
        }
      }
    }),
    [getAllMediaSuccess]: (state, { payload: apartmentBucketObjectArray }) => {
      const indexedBucketObjects: ApartmentIdIndex<
        BucketObject[]
      > = apartmentBucketObjectArray.reduce(
        (
          filesObject: ApartmentIdIndex<BucketObject[]>,
          apartmentMediaObject: [string, BucketObject[]]
        ) => {
          filesObject[apartmentMediaObject[0]] = apartmentMediaObject[1]
          return filesObject
        },
        {}
      )
      return {
        ...state,
        files: indexedBucketObjects
      }
    },
    [deleteApartmentMediaSuccess]: (
      state,
      { payload: { apartmentId, key } }: Action
    ) => {
      const files = state.files[apartmentId].filter(
        (file: BucketObject) => file.key !== key
      )
      return {
        ...state,
        files: {
          ...state.files,
          [apartmentId]: files
        }
      }
    },
    [updateApartmentMediaSuccess]: (
      state,
      { payload: apartment }: Action<Apartment>
    ) => {
      return {
        ...state,
        apartments: {
          ...state.apartments,
          [apartment.apartmentId]: {
            ...apartment,
            switchedData:
              state?.apartments[apartment.apartmentId]?.switchedData || null
          }
        }
      }
    },
    [initializeBeforeLoginApartmentSuccess]: (
      state,
      { payload: beforeLoginApartment }: Action<BeforeLoginApartment>
    ) => ({
      ...state,
      beforeLoginApartment,
      beforeLoginMedia: []
    }),
    [saveBeforeLoginApartmentInfoSuccess]: (
      state,
      { payload: beforeLoginApartment }: Action<BeforeLoginApartment>
    ) => ({
      ...state,
      beforeLoginApartment: {
        ...state.beforeLoginApartment,
        ...beforeLoginApartment
      }
    }),
    [saveInstantRentApplicationInfoSuccess]: (
      state,
      { payload: beforeLoginApartment }: Action<BeforeLoginApartment>
    ) => ({
      ...state,
      beforeLoginApartment: {
        ...state.beforeLoginApartment,
        ...beforeLoginApartment
      }
    }),
    [addBeforeLoginApartmentMediaSuccess]: (
      state,
      { payload: newBeforeLoginMedia }: Action<ImageFileWithObjectURL[]>
    ) => ({
      ...state,
      beforeLoginMedia: newBeforeLoginMedia
    }),
    [deleteBeforeLoginApartmentMediaSuccess]: (
      state,
      { payload: newBeforeLoginMedia }: Action<ImageFileWithObjectURL[]>
    ) => ({
      ...state,
      beforeLoginMedia: newBeforeLoginMedia
    }),
    [reorderBeforeLoginApartmentMediaSuccess]: (
      state,
      { payload: newBeforeLoginMedia }: Action<ImageFileWithObjectURL[]>
    ) => ({
      ...state,
      beforeLoginMedia: newBeforeLoginMedia
    }),
    [confirmBeforeLoginApartmentMediaSuccess]: (
      state,
      { payload: beforeLoginApartment }: Action<BeforeLoginApartment>
    ) => ({
      ...state,
      beforeLoginApartment: {
        ...state.beforeLoginApartment,
        ...beforeLoginApartment
      }
    }),
    [confirmBeforeLoginApartmentSuccess]: (
      state,
      { payload: beforeLoginApartment }: Action<BeforeLoginApartment>
    ) => ({
      ...state,
      beforeLoginApartment: {
        ...state.beforeLoginApartment,
        ...beforeLoginApartment
      }
    }),
    [signUpBeforeLoginSuccess]: (state) => ({
      ...state,
      beforeLoginApartment: undefined,
      beforeLoginMedia: []
    }),
    [emptyBeforeLoginState]: (state) => ({
      ...state,
      beforeLoginApartment: undefined,
      beforeLoginMedia: []
    }),
    [addSwitchedData]: (state, { payload }) => {
      if (payload.apartment) {
        const { apartment, ...restPayload } = payload
        return {
          ...state,
          apartments: {
            ...state.apartments,
            [apartment.apartmentId]: {
              ...state.apartments[apartment.apartmentId],
              switchedData: restPayload
            }
          }
        }
      } else
        return {
          ...state,
          switchedData: payload
        }
    }
  },
  initialState
)

export default apartmentsReducer
